import useMotionPreference from "@/hooks/useMotionPreference";
import React, { useRef, useEffect, useState } from "react";
import { motion, useInView, useScroll, useTransform, animate, useMotionValue, useSpring, useMotionTemplate } from "framer-motion";

const easing = [0.22, 1, 0.36, 1];

export const Reveal = ({ children, delay = 0, y = 28, className = "", once = true }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once, margin: "-80px" });
  const reduceMotion = useMotionPreference();
  return (
    <motion.div
      ref={ref}
      className={className}
      initial={reduceMotion ? false : { opacity: 0, y }}
      animate={inView || reduceMotion ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: reduceMotion ? 0 : 0.8, delay: reduceMotion ? 0 : delay, ease: easing }}
    >
      {children}
    </motion.div>
  );
};

export const stagger = {
  hidden: {},
  show: { transition: { staggerChildren: 0.08 } },
};
export const item = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: easing } },
};

export const Counter = ({ to = 0, suffix = "", decimals = 0, duration = 1.8 }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-40px" });
  const [val, setVal] = useState(0);
  const currentValue = useRef(0);
  const reduceMotion = useMotionPreference();
  useEffect(() => {
    if (reduceMotion) { currentValue.current = to; return; }
    if (!inView) return;
    const controls = animate(currentValue.current, to, {
      duration,
      ease: "easeOut",
      onUpdate: (v) => {
        currentValue.current = v;
        setVal(decimals > 0 ? parseFloat(v.toFixed(decimals)) : Math.floor(v));
      },
    });
    return () => controls.stop();
  }, [inView, to, decimals, duration, reduceMotion]);
  const displayedValue = reduceMotion ? to : val;
  return (
    <span ref={ref}>
      {decimals > 0 ? displayedValue.toFixed(decimals) : displayedValue.toLocaleString()}
      {suffix}
    </span>
  );
};

export function AnimatedBar({ value = 0, label, valueText, className = "" }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.5 });
  const reduceMotion = useMotionPreference();
  const percent = Math.max(0, Math.min(100, Number(value) || 0));
  return (
    <div
      ref={ref}
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={Math.round(percent)}
      aria-valuetext={valueText}
      className="h-full w-full"
    >
      <motion.div
        className={`h-full w-full origin-left rounded-full ${className}`}
        initial={reduceMotion ? false : { scaleX: 0 }}
        animate={{ scaleX: inView || reduceMotion ? percent / 100 : 0 }}
        transition={{ duration: reduceMotion ? 0 : 0.8, ease: easing }}
      />
    </div>
  );
}

export const useParallax = (ref, distance = 80) => {
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  return useTransform(scrollYProgress, [0, 1], [distance, -distance]);
};

export const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`relative mx-auto w-full max-w-7xl px-6 md:px-10 ${className}`}>
    {children}
  </section>
);

export const Overline = ({ children, num }) => {
  const reduceMotion = useMotionPreference();
  return (
  <div className="mb-4 flex items-center gap-3 font-mono text-xs uppercase tracking-[0.25em] text-brand-500">
    {num && <span className="text-white/30">{num}</span>}
    <motion.span
      aria-hidden="true"
      className="h-px w-8 origin-left bg-brand-500/50"
      initial={reduceMotion ? false : { scaleX: 0 }}
      whileInView={{ scaleX: 1 }}
      viewport={{ once: true, amount: 1 }}
      transition={{ duration: reduceMotion ? 0 : 0.65, ease: easing }}
    />
    {children}
  </div>
  );
};

const canTrackPointer = (event) =>
  event.pointerType === "mouse" && window.matchMedia("(hover: hover) and (pointer: fine)").matches;

/** Keep the link itself semantic and focusable; only desktop pointers attract it. */
export function MagneticLink({ children, className = "", ...props }) {
  const reduceMotion = useMotionPreference();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const springX = useSpring(x, { stiffness: 260, damping: 20 });
  const springY = useSpring(y, { stiffness: 260, damping: 20 });
  const reset = () => { x.set(0); y.set(0); };

  return (
    <motion.a
      {...props}
      className={`focus-ring ${className}`}
      style={{ x: reduceMotion ? 0 : springX, y: reduceMotion ? 0 : springY }}
      onPointerMove={(event) => {
        if (reduceMotion || !canTrackPointer(event)) return;
        const rect = event.currentTarget.getBoundingClientRect();
        x.set(Math.max(-6, Math.min(6, (event.clientX - rect.left - rect.width / 2) * 0.12)));
        y.set(Math.max(-4, Math.min(4, (event.clientY - rect.top - rect.height / 2) * 0.12)));
      }}
      onPointerLeave={reset}
      onPointerCancel={reset}
      onBlur={reset}
    >
      {children}
    </motion.a>
  );
}

export function SpotlightCard({ children, className = "", ...props }) {
  const reduceMotion = useMotionPreference();
  const x = useMotionValue(0);
  const y = useMotionValue(0);
  const opacity = useMotionValue(0);
  const background = useMotionTemplate`radial-gradient(420px circle at ${x}px ${y}px, rgb(var(--brand-500)/0.13), transparent 75%)`;

  return (
    <article
      {...props}
      className={`relative ${className}`}
      onPointerMove={(event) => {
        if (reduceMotion || !canTrackPointer(event)) return;
        const rect = event.currentTarget.getBoundingClientRect();
        x.set(event.clientX - rect.left);
        y.set(event.clientY - rect.top);
        opacity.set(1);
      }}
      onPointerLeave={() => opacity.set(0)}
      onPointerCancel={() => opacity.set(0)}
    >
      {children}
      <motion.div aria-hidden="true" className="pointer-events-none absolute inset-0 z-10 rounded-[inherit]" style={{ background, opacity: reduceMotion ? 0 : opacity }} />
    </article>
  );
}

export function ImageReveal({ children, className = "", ...props }) {
  const reduceMotion = useMotionPreference();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.15 });
  return (
    <div ref={ref} className={className} {...props}>
    <motion.div
      className="relative h-72 w-full lg:absolute lg:inset-0 lg:h-full"
      initial={reduceMotion ? false : { clipPath: "inset(0 0 100% 0)" }}
      animate={inView || reduceMotion ? { clipPath: "inset(0 0 0% 0)" } : {}}
      transition={{ duration: reduceMotion ? 0 : 0.9, ease: easing }}
    >
      {children}
    </motion.div>
    </div>
  );
}
